import React, { useState, useMemo } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import sortBy from 'lodash/sortBy';
import { withStyles } from '@material-ui/core/styles';

import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableSortLabel from '@material-ui/core/TableSortLabel';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableFoot from '@material-ui/core/TableFooter';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiPaper from '@material-ui/core/Paper';
import MuiBox from '@material-ui/core/Box';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import TablePagination from '@material-ui/core/TablePagination';
import DialogConfirmInvoiceSplit from 'components/DialogConfirmInvoiceSplit';
import EditNoteDialog from './EditNoteDialog';
import RowButtons from './row-buttons';
import ColumnFilter from 'components/ColumnFilter';
import ActiveFilters from 'components/ActiveFilters';
import useColumnFilters from 'hooks/use-column-filters';

import Row from './table-report-invoice-row';

import { ReportInvoices } from 'api';
const { getAll: getReportInvoices, patch, approve, exportInvoiceCsv } = ReportInvoices();

const styles = () => ({
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
});

const TableReportInvoices = ({ classes, page = 0, showUnapproved }) => {
  const [formData, setFormData] = useState({
    incoming_payment_native: '',
    incoming_payment_usd: '',
  });

  const [params, setParams] = useState({
    page_index: Number(page) || 0,
    page_size: 250,
    sort_by: 'id',
    sort_dir: 'asc',
  });

  // Column filters configuration - principais: DSP, ID e Currency
  const columnFiltersConfig = useMemo(
    () => [
      {
        id: 'id',
        label: 'ID',
        filterType: 'text',
        apiParam: 'report_invoice_id',
      },
      {
        id: 'dsp_name',
        label: 'DSP',
        filterType: 'text',
        apiParam: 'dsp_name',
      },
      {
        id: 'native_currency_abbv',
        label: 'Currency',
        filterType: 'text',
        apiParam: 'native_currency_abbv',
      },
    ],
    []
  );

  // Filter hooks
  const {
    filters,
    activeFilters,
    filterParams,
    openFilterColumn,
    tempFilters,
    openFilterPopup,
    updateTempFilter,
    applyFilter,
    clearCurrentFilter,
    clearAllFilters,
    hasColumnFilter,
    removeFilter,
  } = useColumnFilters(columnFiltersConfig, 'report-invoices');

  // Combine filter params with existing params
  const combinedParams = useMemo(() => {
    return {
      ...params,
      ...filterParams,
    };
  }, [params, filterParams]);

  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [dialogMeta, setDialogMeta] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editingNotesId, setEditingNotesId] = useState(-1);
  const [expandedRow, setExpandedRow] = useState(null);
  const [splitError, setSplitError] = useState(null);

  const history = useHistory();

  const resetStates = async () => {
    await refetchReportInvoices();
    resetPatchStatus();
    resetApproveStatus();
    resetArchiveStatus();
    resetExportStatus();
    setEditingId(null);
  };

  const {
    data,
    isLoading,
    refetch: refetchReportInvoices,
  } = useQuery(
    ['report-invoices', showUnapproved, { ...combinedParams }],
    () => getReportInvoices({ unapproved: showUnapproved, ...combinedParams }),
    {
      select: data => {
        return sortBy(data, ['id']);
      },
      retry: 1,
      retryDelay: 1000,
    }
  );

  const { dataReportInvoices, total } = useMemo(() => {
    let invoiceData = [];
    let totalCount = 0;

    if (Array.isArray(data)) {
      if (data.length > 0) {
        if (Array.isArray(data[0])) {
          invoiceData = data[0];
        } else if (data[0] && typeof data[0] === 'object') {
          invoiceData = [data[0]];
        }
      }

      if (data.length > 1 && typeof data[1] === 'number') {
        totalCount = data[1];
      } else if (invoiceData.length > 0) {
        totalCount = invoiceData.length;
      }
    }

    return {
      dataReportInvoices: invoiceData,
      total: totalCount,
    };
  }, [data]);

  const {
    mutate: patchInvoice,
    status: patchStatus,
    reset: resetPatchStatus,
  } = useMutation(({ editingId, data }) => patch(editingId, data), {
    onSuccess: resetStates,
  });

  const {
    mutate: approveInvoice,
    status: approveStatus,
    reset: resetApproveStatus,
  } = useMutation(id => approve(id), {
    onSuccess: resetStates,
  });

  const {
    mutate: patchInvoiceArchiveStatus,
    status: archiveStatus,
    reset: resetArchiveStatus,
  } = useMutation(
    ({ reportInvoiceId, shouldBeArchived }) => patch(reportInvoiceId, { is_archived: shouldBeArchived }),
    {
      onSuccess: resetStates,
    }
  );

  const {
    mutate: exportReportInvoiceCsv,
    status: exportStatus,
    reset: resetExportStatus,
  } = useMutation(id => exportInvoiceCsv(id));

  const headCells = useMemo(
    () => [
      {
        id: 'id',
        numeric: true,
        label: 'id',
      },
      {
        id: 'dsp_name',
        numeric: false,
        label: 'DSP',
      },
      {
        id: 'native_currency_abbv',
        numeric: false,
        label: 'Currency',
      },
      {
        id: 'is_approved',
        numeric: false,
        label: 'Approved',
      },
      {
        id: 'exchange_rate_to_usd',
        numeric: true,
        label: 'Ex. Rate',
        unsortable: true, //inverse as to not for a bool on all of the other cells.
      },
      {
        id: 'incoming_payment_native',
        numeric: true,
        label: 'Incoming (Native)',
      },
      {
        id: 'incoming_payment_usd',
        numeric: true,
        label: 'Incoming (USD)',
      },
      {
        id: 'notes',
        numeric: false,
        label: 'Notes',
      },
    ],
    []
  );

  // Listeners
  const handleInvoicesSplit = created => {
    if (created) {
      refetchReportInvoices();
      setExpandedRow(null);
      setSplitError(false);
    } else {
      setSplitError(true);
    }

    setIsConfirmationDialogOpen(false);
  };

  const handleSplitInvoices = (row, matchIds) => {
    setDialogMeta({
      id: row.id,
      dsp_name: row.dsp_name,
      native_currency_abbv: row.native_currency_abbv,
      matchIds,
    });
    setIsConfirmationDialogOpen(true);
  };

  const handleDialogClose = () => setIsConfirmationDialogOpen(false);

  const handleFormSubmit = async data => {
    await patchInvoice({ editingId, data });
  };

  const handleApproveInvoice = async () => {
    const matchingRow = (dataReportInvoices || []).find(invoice => invoice.id === editingId);
    if (!matchingRow) {
      console.error('approval invoice row id could not be determined.');
    }
    await approveInvoice(editingId);
  };

  const handleArchiveInvoice = async () =>
    patchInvoiceArchiveStatus({
      reportInvoiceId: editingId,
      shouldBeArchived: true,
    });

  const handleUnarchiveInvoice = async () =>
    patchInvoiceArchiveStatus({
      reportInvoiceId: editingId,
      shouldBeArchived: false,
    });

  const handleExportClick = async () => {
    await exportReportInvoiceCsv(editingId);
  };

  const handleEditRowSelected = async id => {
    resetPatchStatus();
    resetApproveStatus();
    resetArchiveStatus();
    resetExportStatus();
    setSplitError(false);
    setEditingId(id);
  };

  function handleChangePage(_, page) {
    setParams({ ...params, page_index: page });
    history.push(`/report-invoices?page=${page}`);
  }

  function handleChangeRowsPerPage({ target: { value } }) {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue <= 0) {
      return;
    }

    setParams({
      ...params,
      page_index: 0,
      page_size: numValue,
    });
    history.push('/report-invoices?page=0');
  }

  const handleRequestSort = property => {
    const isAsc = params.sort_by === property && params.sort_dir === 'asc';
    setParams({
      ...params,
      sort_dir: isAsc ? 'desc' : 'asc',
      sort_by: property,
    });
  };

  // Helper function to get filter config for a column
  const getColumnFilterConfig = columnId => {
    return columnFiltersConfig.find(config => config.id === columnId);
  };

  // Filter handlers
  const handleOpenFilterPopup = columnId => {
    openFilterPopup(columnId, {});
  };

  const handleFilterValueChange = (key, value) => {
    updateTempFilter(key, value);
  };

  const handleApplyFilter = () => {
    applyFilter();
  };

  const handleClearFilter = () => {
    clearCurrentFilter();
  };

  const handleRemoveFilter = filterId => {
    removeFilter(filterId);
  };

  const handleClearAllFilters = () => {
    clearAllFilters();
  };

  return (
    <MuiTableContainer
      component={MuiPaper}
      style={{
        overflow: 'hidden',
        overflowX: 'auto',
        maxWidth: '100%',
      }}>
      {isLoading ? (
        <div className={classes.loadWrapper}>
          <MuiCircularProgress />
        </div>
      ) : (
        <div>
          {/* Active Filters */}
          <ActiveFilters
            filters={filters}
            columns={columnFiltersConfig}
            onClearFilter={handleRemoveFilter}
            onClearAllFilters={handleClearAllFilters}
          />

          <MuiTable
            aria-label="collapsible table"
            size="small"
            style={{
              width: '100%',
              tableLayout: 'fixed',
            }}>
            <colgroup>
              <col style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }} /> {/* ID */}
              <col style={{ width: '110px', minWidth: '100px', maxWidth: '110px' }} /> {/* DSP */}
              <col style={{ width: '130px', minWidth: '100px', maxWidth: '130px' }} /> {/* Currency */}
              <col style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }} /> {/* Approved */}
              <col style={{ width: '100px', minWidth: '100px', maxWidth: '100px' }} /> {/* Exchange Rate */}
              <col style={{ width: '130px', minWidth: '100px', maxWidth: '130px' }} /> {/* Incoming (Native) */}
              <col style={{ width: '130px', minWidth: '100px', maxWidth: '130px' }} /> {/* Incoming (USD) */}
              <col style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }} /> {/* Notes */}
              <col style={{ width: '180px', minWidth: '180px', maxWidth: '180px' }} /> {/* Actions */}
            </colgroup>
            <MuiTableHead>
              <MuiTableRow>
                <MuiTableCell colSpan={headCells.length + 1} align="right">
                  <MuiBox display="flex" justifyContent="flex-end">
                    <TablePagination
                      rowsPerPageOptions={[250, 500, 1000]}
                      rowsPerPage={params.page_size}
                      count={total || 0}
                      page={params.page_index}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </MuiBox>
                </MuiTableCell>
              </MuiTableRow>
              <MuiTableRow>
                {headCells.map(headCell => {
                  const filterConfig = getColumnFilterConfig(headCell.id);
                  return (
                    <MuiTableCell
                      key={headCell.id}
                      align={'left'}
                      sortDirection={params.sort_by === headCell.id ? params.sort_dir : false}>
                      <MuiBox display="flex" alignItems="center">
                        {!headCell.unsortable ? (
                          <MuiTableSortLabel
                            active={params.sort_by === headCell.id}
                            direction={params.sort_by === headCell.id ? params.sort_dir : 'asc'}
                            onClick={_ => handleRequestSort(headCell.id)}>
                            {headCell.label}
                          </MuiTableSortLabel>
                        ) : (
                          <>{headCell.label}</>
                        )}

                        {/* Column Filter */}
                        {filterConfig && (
                          <ColumnFilter
                            columnId={headCell.id}
                            columnLabel={headCell.label}
                            filterType={filterConfig.filterType}
                            hasFilter={hasColumnFilter(headCell.id)}
                            onOpenFilter={handleOpenFilterPopup}
                            onApplyFilter={handleApplyFilter}
                            onClearFilter={handleClearFilter}
                            filterValues={openFilterColumn?.id === headCell.id ? tempFilters : {}}
                            onFilterValueChange={handleFilterValueChange}
                            options={filterConfig.options}
                          />
                        )}
                      </MuiBox>
                    </MuiTableCell>
                  );
                })}
                <MuiTableCell></MuiTableCell>
              </MuiTableRow>
            </MuiTableHead>
            <MuiTableBody>
              {(dataReportInvoices || []).map(row => (
                <Row
                  key={row.id}
                  index={row.id}
                  row={row}
                  editing={editingId === row.id}
                  onSplitInvoices={handleSplitInvoices}
                  setEditingNotesId={setEditingNotesId}
                  expandedRow={expandedRow}
                  setExpandedRow={setExpandedRow}
                  formData={formData}
                  setFormData={setFormData}
                  splitError={splitError}>
                  <RowButtons
                    row={row}
                    formData={formData}
                    isArchived={row.is_archived}
                    editing={editingId === row.id}
                    onEditChange={() => handleEditRowSelected(editingId !== row.id ? row.id : null)}
                    onApproveInvoice={handleApproveInvoice}
                    onArchiveInvoice={handleArchiveInvoice}
                    onUnarchiveInvoice={handleUnarchiveInvoice}
                    onExportClick={handleExportClick}
                    onSaveIncomingPayments={() => handleFormSubmit(formData)}
                    patchStatus={patchStatus}
                    archiveStatus={archiveStatus}
                    approveStatus={approveStatus}
                    exportStatus={exportStatus}
                  />
                </Row>
              ))}
            </MuiTableBody>
            <MuiTableFoot>
              <MuiTableRow>
                <MuiTableCell colSpan={headCells.length + 1} align="right">
                  <MuiBox display="flex" justifyContent="flex-end">
                    <TablePagination
                      rowsPerPageOptions={[250, 500, 1000]}
                      rowsPerPage={params.page_size}
                      count={total || 0}
                      page={params.page_index}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                  </MuiBox>
                </MuiTableCell>
              </MuiTableRow>
            </MuiTableFoot>
          </MuiTable>
        </div>
      )}
      <EditNoteDialog
        handleDialogClose={() => {
          setEditingNotesId(-1);
        }}
        editingNotesId={editingNotesId}
        notes={dataReportInvoices?.find(ri => ri.id === editingNotesId)?.notes}
        refetchReportInvoices={refetchReportInvoices}
      />
      <DialogConfirmInvoiceSplit
        open={isConfirmationDialogOpen}
        onClose={handleDialogClose}
        onCreated={handleInvoicesSplit}
        meta={dialogMeta}
      />
    </MuiTableContainer>
  );
};

export default withStyles(styles)(withRouter(TableReportInvoices));
